import * as React from "react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import camelCase from "lodash/camelCase";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import moment from "moment";
import { OrderedMap } from "immutable";
import { describeExpression } from "util/CronUtils";
import { getPathnameWithoutId } from "util/URLUtils";
import { isPermitted } from "util/PermissionsMixin";
import * as FormsUtils from "util/FormsUtils";
import FormWarningsContext from "contexts/FormWarningsContext";
import { useStore } from "stores/connect";
import Store from "logic/local-storage/Store";
import { MultiSelect, TimeUnitInput, SearchFiltersFormControls, TimezoneSelect } from "components/common";
import Query from "views/logic/queries/Query";
import Search from "views/logic/search/Search";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { Alert, ButtonToolbar, ControlLabel, FormGroup, HelpBlock, Input } from "components/bootstrap";
import RelativeTime from "components/common/RelativeTime";
import LookupTableParameter from "views/logic/parameters/LookupTableParameter";
import { LookupTablesActions, LookupTablesStore } from "stores/lookup-tables/LookupTablesStore";
import validateQuery from "views/components/searchbar/queryvalidation/validateQuery";
import generateId from "logic/generateId";
import parseSearch from "views/logic/slices/parseSearch";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useUserDateTime from "hooks/useUserDateTime";
import { indicesInWarmTier, isSearchingWarmTier } from "views/components/searchbar/queryvalidation/warmTierValidation";
import { defaultCompare } from "logic/DefaultCompare";
import EditQueryParameterModal from "../event-definition-form/EditQueryParameterModal";
import commonStyles from "../common/commonStyles.css";
export const PLUGGABLE_CONTROLS_HIDDEN_KEY = "pluggableSearchBarControlsAreHidden";
export const TIME_UNITS = ["HOURS", "MINUTES", "SECONDS"];
const LOOKUP_PERMISSIONS = ["lookuptables:read"];
const STREAM_PERMISSIONS = ["streams:read"];
const buildNewParameter = (name) => ({
  name,
  embryonic: true,
  type: "lut-parameter-v1",
  data_type: "any",
  title: "new title"
});
const toTimeRange = (from) => ({
  type: "relative",
  from: from / 1e3
});
const WarmTierTimeStamp = () => {
  const { warnings } = useContext(FormWarningsContext);
  const validationState = warnings == null ? void 0 : warnings.queryString;
  const warmTierRanges = indicesInWarmTier(validationState);
  const latestWarmTierRangeEnd = warmTierRanges.map((range) => range.end).sort((a, b) => b - a)[0];
  return /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: latestWarmTierRangeEnd });
};
const StreamCategorySelector = ({ onChange, streams, value }) => {
  const streamCategoryOptions = useMemo(
    () => [...new Set(streams.flatMap((stream) => {
      var _a;
      return (_a = stream == null ? void 0 : stream.categories) != null ? _a : [];
    }))].sort(defaultCompare).map((category) => ({ label: category, value: category })),
    [streams]
  );
  if (!streamCategoryOptions || streamCategoryOptions.length === 0) return null;
  return /* @__PURE__ */ React.createElement(FormGroup, { controlId: "filter-stream-categories" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Stream Categories ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(MultiSelect, { id: "filter-stream-categories", onChange, options: streamCategoryOptions, value }), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select stream categories the search should include."));
};
const QueryParameters = ({ eventDefinition, onChange, userCanViewLookupTables, validation }) => {
  var _a, _b;
  const { tables = {} } = useStore(LookupTablesStore);
  const queryParameters = (_b = (_a = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a.query_parameters) != null ? _b : [];
  const onChangeQueryParameters = useCallback(
    (newQueryParameters) => {
      const newConfig = { ...eventDefinition.config, query_parameters: newQueryParameters || [] };
      return onChange(newConfig);
    },
    [eventDefinition.config, onChange]
  );
  if (!userCanViewLookupTables) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, "This account lacks permission to declare Query Parameters from Lookup Tables.");
  }
  const parameterButtons = queryParameters.map((queryParam) => /* @__PURE__ */ React.createElement(
    EditQueryParameterModal,
    {
      key: queryParam.name,
      queryParameter: LookupTableParameter.fromJSON(queryParam),
      embryonic: !!queryParam.embryonic,
      queryParameters,
      lookupTables: Object.values(tables),
      onChange: onChangeQueryParameters
    }
  ));
  if (isEmpty(parameterButtons)) {
    return null;
  }
  const hasEmbryonicParameters = !isEmpty(
    queryParameters.filter((param) => param.embryonic)
  );
  return /* @__PURE__ */ React.createElement(FormGroup, { validationState: validation.errors.query_parameters ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Query Parameters"), /* @__PURE__ */ React.createElement(Alert, { bsStyle: hasEmbryonicParameters ? "danger" : "info" }, /* @__PURE__ */ React.createElement(ButtonToolbar, null, parameterButtons)), hasEmbryonicParameters && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.query_parameters ? validation == null ? void 0 : validation.errors.query_parameters[0] : "Please declare missing query parameters by clicking on the buttons above."));
};
const FilterForm = ({ currentUser, eventDefinition, onChange, streams, validation }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  const { execute_every_ms: executeEveryMs, search_within_ms: searchWithinMs } = eventDefinition.config;
  const [currentConfig, setCurrentConfig] = useState(eventDefinition.config);
  const searchWithin = extractDurationAndUnit(searchWithinMs, TIME_UNITS);
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const { userTimezone } = useUserDateTime();
  const { setFieldWarning, warnings } = useContext(FormWarningsContext);
  const validationState = warnings == null ? void 0 : warnings.queryString;
  const warmTierRanges = indicesInWarmTier(validationState);
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const queryId = generateId();
  const searchTypeId = generateId();
  const [queryParameterStash, setQueryParameterStash] = useState({});
  const [searchFiltersHidden, setSearchFiltersHidden] = useState(false);
  const [searchWithinMsUnit, setSearchWithinMsUnit] = useState(searchWithin.unit);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const [searchWithinMsDuration, setSearchWithinMsDuration] = useState(searchWithin.duration);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const userCanViewLookupTables = useMemo(
    () => isPermitted(currentUser.permissions, LOOKUP_PERMISSIONS),
    [currentUser.permissions]
  );
  const isStreamRequired = useMemo(
    () => !isPermitted(currentUser.permissions, STREAM_PERMISSIONS),
    [currentUser.permissions]
  );
  const [cronDescription, setCronDescription] = useState(
    currentConfig.cron_expression ? describeExpression(currentConfig.cron_expression) : ""
  );
  const validateQueryString = useCallback(
    (queryString, streamIds, timeRange, timezone) => {
      const request = {
        timeRange,
        queryString,
        streams: streamIds
      };
      validateQuery(request, timezone).then((result) => {
        if ((result == null ? void 0 : result.status) === "WARNING" || (result == null ? void 0 : result.status) === "ERROR") {
          setFieldWarning("queryString", result);
        } else {
          setFieldWarning("queryString", void 0);
        }
      });
    },
    [setFieldWarning]
  );
  useEffect(() => {
    if (userCanViewLookupTables) {
      LookupTablesActions.searchPaginated(1, 0, void 0, false);
    }
  }, [userCanViewLookupTables]);
  useEffect(() => {
    validateQueryString(
      eventDefinition.config.query,
      eventDefinition.config.streams,
      toTimeRange(eventDefinition.config.search_within_ms),
      userTimezone
    );
  }, [
    eventDefinition.config.query,
    eventDefinition.config.streams,
    eventDefinition.config.search_within_ms,
    setFieldWarning,
    userTimezone,
    validateQueryString
  ]);
  const getUpdatedConfig = useCallback(
    (key, value) => {
      const config = cloneDeep(eventDefinition.config);
      config[key] = value;
      setCurrentConfig(config);
      return config;
    },
    [eventDefinition.config]
  );
  const propagateChange = useCallback(
    (config) => {
      onChange("config", config);
    },
    [onChange]
  );
  const syncParamsWithQuery = useCallback(
    (paramsInQuery, config) => {
      const queryParameters = (config == null ? void 0 : config.query_parameters) || [];
      const keptParameters = [];
      const staleParameters = {};
      queryParameters.forEach((p) => {
        if (paramsInQuery.has(p.name)) {
          keptParameters.push(p);
        } else {
          staleParameters[p.name] = p;
        }
      });
      const newParameters = [];
      paramsInQuery.forEach((np) => {
        if (!keptParameters.find((p) => p.name === np)) {
          if (queryParameterStash[np]) {
            newParameters.push(queryParameterStash[np]);
          } else {
            newParameters.push(buildNewParameter(np));
          }
        }
      });
      setQueryParameterStash(merge(queryParameterStash, staleParameters));
      propagateChange({ ...config, query_parameters: keptParameters.concat(newParameters) });
    },
    [propagateChange, queryParameterStash]
  );
  const parseQuery = useCallback(
    (queryString, config, searchFilters = OrderedMap()) => {
      if (!userCanViewLookupTables) {
        return;
      }
      const queryBuilder = Query.builder().id(queryId).query({ type: "elasticsearch", query_string: queryString }).timerange({ type: "relative", range: 1e3 }).filters(searchFilters.toList()).searchTypes([
        {
          id: searchTypeId,
          type: "messages",
          limit: 10,
          offset: 0,
          filter: void 0,
          filters: void 0,
          name: void 0,
          query: void 0,
          timerange: void 0,
          streams: void 0,
          stream_categories: void 0,
          sort: [],
          decorators: []
        }
      ]);
      const query = queryBuilder.build();
      const search = Search.create().toBuilder().queries([query]).build();
      parseSearch(search).then((res) => {
        syncParamsWithQuery(res.undeclared, config);
      });
    },
    [queryId, searchTypeId, syncParamsWithQuery, userCanViewLookupTables]
  );
  const debouncedParseQuery = debounce(parseQuery, 250);
  const handleConfigChange = useCallback(
    (name, config) => {
      if (name === "_is_scheduled") {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_EXECUTED_AUTOMATICALLY_TOGGLED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "event-definition-condition",
          app_action_value: "enable-checkbox",
          is_scheduled: config._is_scheduled
        });
      }
      propagateChange(config);
    },
    [pathname, propagateChange, sendTelemetry]
  );
  const handleQueryChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
      debouncedParseQuery(value, newConfig);
    },
    [debouncedParseQuery, getUpdatedConfig, handleConfigChange]
  );
  const handleCronExpressionChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleCronTimezoneChange = useCallback(
    (tz) => {
      const newConfig = getUpdatedConfig("cron_timezone", tz);
      handleConfigChange("cron_timezone", newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleSearchFiltersChange = useCallback(
    (searchFilters) => {
      const { query } = eventDefinition.config;
      const newConfig = getUpdatedConfig("filters", searchFilters.toArray());
      propagateChange(newConfig);
      debouncedParseQuery(query, newConfig);
    },
    [debouncedParseQuery, eventDefinition.config, getUpdatedConfig, propagateChange]
  );
  const handleEnabledChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = getUpdatedConfig(name, value);
      handleConfigChange(name, newConfig);
    },
    [getUpdatedConfig, handleConfigChange]
  );
  const handleUseCronSchedulingChange = useCallback(
    (event) => {
      const { name } = event.target;
      const value = FormsUtils.getValueFromInput(event.target);
      const newConfig = cloneDeep(eventDefinition.config);
      newConfig[name] = value;
      if (value) {
        newConfig.cron_expression = "";
        newConfig.cron_timezone = userTimezone;
      } else {
        newConfig.cron_expression = null;
        newConfig.cron_timezone = null;
      }
      setCurrentConfig(newConfig);
      propagateChange(newConfig);
    },
    [eventDefinition.config, propagateChange, userTimezone]
  );
  const hideFiltersPreview = useCallback((value) => {
    Store.set(PLUGGABLE_CONTROLS_HIDDEN_KEY, value);
    setSearchFiltersHidden(value);
  }, []);
  const handleStreamsChange = useCallback(
    (nextValue) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_STREAM_SELECTED, {
        app_pathname: getPathnameWithoutId(pathname),
        app_section: "event-definition-condition",
        app_action_value: "stream-select"
      });
      propagateChange(getUpdatedConfig("streams", nextValue));
    },
    [getUpdatedConfig, pathname, propagateChange, sendTelemetry]
  );
  const handleTimeRangeChange = useCallback(
    (fieldName) => (nextValue, nextUnit) => {
      if (fieldName === "search_within_ms" && nextUnit !== searchWithinMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_SEARCH_WITHIN_THE_LAST_UNIT_CHANGED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "event-definition-condition",
          app_action_value: "searchWithinMsUnit-select",
          new_unit: nextUnit
        });
      } else if (fieldName === "execute_every_ms" && nextUnit !== executeEveryMsUnit) {
        sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_CONDITION.FILTER_EXECUTE_SEARCH_EVERY_UNIT_CHANGED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "event-definition-condition",
          app_action_value: "executeEveryMsUnit-select",
          new_unit: nextUnit
        });
      }
      const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
      propagateChange(getUpdatedConfig(fieldName, durationInMs));
      const stateFieldName = camelCase(fieldName);
      if (stateFieldName === "searchWithinMs") {
        setSearchWithinMsDuration(nextValue);
        setSearchWithinMsUnit(nextUnit);
        return;
      }
      setExecuteEveryMsDuration(nextValue);
      setExecuteEveryMsUnit(nextUnit);
    },
    [executeEveryMsUnit, getUpdatedConfig, pathname, propagateChange, searchWithinMsUnit, sendTelemetry]
  );
  const onlyFilters = eventDefinition._scope === "ILLUMINATE";
  const formattedStreams = [...streams.map((s) => s.id), ...(_b = (_a = eventDefinition == null ? void 0 : eventDefinition.config) == null ? void 0 : _a.streams) != null ? _b : []].map((streamId) => {
    var _a2;
    const stream = streams.find((s) => s.id === streamId);
    return { label: (_a2 = stream == null ? void 0 : stream.title) != null ? _a2 : streamId, value: streamId };
  }).sort((s1, s2) => defaultCompare(s1.label, s2.label));
  return /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Filter"), /* @__PURE__ */ React.createElement("p", null, "Add information to filter the log messages that are relevant for this Event Definition."), onlyFilters || /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "filter-query",
      name: "query",
      label: "Search Query",
      type: "text",
      help: /* @__PURE__ */ React.createElement("span", null, "Search query that Messages should match. You can use the same syntax as in the Search page, including declaring Query Parameters from Lookup Tables by using the ", /* @__PURE__ */ React.createElement("code", null, "$newParameter$"), " syntax."),
      value: (_c = currentConfig.query) != null ? _c : "",
      onChange: handleQueryChange
    }
  ), onlyFilters || /* @__PURE__ */ React.createElement(
    QueryParameters,
    {
      eventDefinition,
      onChange: propagateChange,
      userCanViewLookupTables,
      validation
    }
  ), !searchFiltersHidden && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-filters" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Search Filters ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement("div", { style: { margin: "16px 0" } }, /* @__PURE__ */ React.createElement(
    SearchFiltersFormControls,
    {
      filters: eventDefinition.config.filters,
      onChange: handleSearchFiltersChange,
      hideFiltersPreview
    }
  ))), onlyFilters || /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "filter-streams" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Streams", !isStreamRequired && /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, " (Optional)")), /* @__PURE__ */ React.createElement(
    MultiSelect,
    {
      id: "filter-streams",
      required: isStreamRequired,
      onChange: (selected) => handleStreamsChange(selected === "" ? [] : selected.split(",")),
      options: formattedStreams,
      value: ((_d = eventDefinition.config.streams) != null ? _d : []).join(",")
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Select streams the search should include. Searches in all streams if empty.")), /* @__PURE__ */ React.createElement(
    StreamCategorySelector,
    {
      onChange: (selected) => propagateChange(getUpdatedConfig("stream_categories", selected === "" ? [] : selected.split(","))),
      value: ((_e = eventDefinition.config.stream_categories) != null ? _e : []).join(","),
      streams
    }
  ), isSearchingWarmTier(warmTierRanges) && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: "Warm Tier Warning" }, "The selected time range will include data stored in the Warm Tier. Events that must frequently retrieve data from the Warm Tier may cause performance problems. A value for", " ", /* @__PURE__ */ React.createElement("strong", null, "Search within the last"), " exceeding the following duration will fall into the Warm Tier:", " ", /* @__PURE__ */ React.createElement(WarmTierTimeStamp, null), "."), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-within", validationState: validation.errors.search_within_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Search within the last",
      update: handleTimeRangeChange("search_within_ms"),
      value: searchWithinMsDuration,
      unit: searchWithinMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.search_within_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.search_within_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: "Use Cron Scheduling",
      help: "Schedule this event with a Quartz cron expression",
      checked: (_f = eventDefinition.config.use_cron_scheduling) != null ? _f : false,
      onChange: handleUseCronSchedulingChange
    }
  ), currentConfig.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "cron-expression",
      validationState: validation.errors.cron_expression ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "cron-expression",
        name: "cron_expression",
        label: "Cron Expression",
        type: "text",
        help: /* @__PURE__ */ React.createElement("span", null, cronDescription || "A Quartz cron expression to determine when the event should be run."),
        value: (_g = currentConfig.cron_expression) != null ? _g : "",
        onBlur: () => setCronDescription(describeExpression(currentConfig.cron_expression)),
        onChange: handleCronExpressionChange
      }
    ),
    validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.cron_expression[0])
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Cron Time Zone"), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      value: (_h = currentConfig.cron_timezone) != null ? _h : userTimezone,
      name: "cron_timezone",
      clearable: false,
      onChange: handleCronTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(FormGroup, { controlId: "execute-every", validationState: validation.errors.execute_every_ms ? "error" : null }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: "Execute search every",
      update: handleTimeRangeChange("execute_every_ms"),
      value: executeEveryMsDuration,
      unit: executeEveryMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: "Enable",
      help: "Should this event definition be executed automatically?",
      checked: (_i = eventDefinition.config._is_scheduled) != null ? _i : true,
      onChange: handleEnabledChange
    }
  )));
};
export default FilterForm;
